import React from 'react';

interface ChatHistoryItem {
  id: string;
  title: string;
  messageCount: number;
  lastUpdated: string;
}

interface SidebarProps {
  chats: ChatHistoryItem[];
  currentChatId: string | null;
  editingChatId: string | null;
  editingTitle: string;
  searchQuery: string;
  onNewChat: () => void;
  onLoadChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
  onEditChat: (chatId: string, currentTitle: string) => void;
  onDownloadChat: (chatId: string) => void;
  onSaveEdit: (chatId: string) => void;
  onCancelEdit: () => void;
  onEditingTitleChange: (value: string) => void;
  onSearchQueryChange: (value: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  chats,
  currentChatId,
  editingChatId,
  editingTitle,
  searchQuery,
  onNewChat,
  onLoadChat,
  onDeleteChat,
  onEditChat,
  onDownloadChat,
  onSaveEdit,
  onCancelEdit,
  onEditingTitleChange,
  onSearchQueryChange,
}) => (
  <aside className="chat-history-sidebar" aria-label="Chat history">
    <div className="chat-history-header">
      <div className="nav-section-title">Chat History</div>
    </div>

    <div className="chat-controls">
      <button className="new-chat-btn-plus" onClick={onNewChat} title="New Chat" aria-label="Start new chat">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>

      <input
        type="text"
        placeholder="Search..."
        value={searchQuery}
        onChange={(event) => onSearchQueryChange(event.target.value)}
        className="chat-search-input"
      />
    </div>

    <div className="sidebar-chats">
      {chats.length === 0 ? (
        <div className="sidebar-empty">
          <p>No chats yet</p>
          <span>Start a new conversation</span>
        </div>
      ) : (
        chats.map((chat) => (
          <div
            key={chat.id}
            className={`sidebar-chat-item ${currentChatId === chat.id ? 'active' : ''}`}
          >
            {editingChatId === chat.id ? (
              <div className="chat-edit-mode">
                <input
                  type="text"
                  value={editingTitle}
                  onChange={(event) => onEditingTitleChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') onSaveEdit(chat.id);
                    if (event.key === 'Escape') onCancelEdit();
                  }}
                  autoFocus
                  className="chat-edit-input"
                />
                <button onClick={() => onSaveEdit(chat.id)} className="chat-edit-save" aria-label="Save chat title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="chat-item-content" onClick={() => onLoadChat(chat.id)}>
                <div className="chat-item-text">
                  <div className="chat-item-title">{chat.title}</div>
                  <div className="chat-item-meta-row">
                    <span className="chat-item-meta">{chat.messageCount} Messages - {new Date(chat.lastUpdated).toLocaleDateString()}</span>
                    <div className="chat-item-actions">
                      <button onClick={(event) => { event.stopPropagation(); onEditChat(chat.id, chat.title); }} title="Edit" className="chat-action-icon">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button onClick={(event) => { event.stopPropagation(); onDownloadChat(chat.id); }} title="Download" className="chat-action-icon">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      </button>
                      <button onClick={(event) => { event.stopPropagation(); if (window.confirm('Delete this chat?')) onDeleteChat(chat.id); }} title="Delete" className="chat-action-icon delete">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  </aside>
);

export default Sidebar;
